import { useMemo } from 'react';
import { MDText } from 'i18n-react';

import cypressIds from 'Components/cypress_ids.json';
import Expandable from 'Components/shared/Expandable/Expandable';
import { triggerValuationsToggleEvent } from 'Services/analytics/events';
import { formatDate } from 'Utilities/formatters';
import { applyCypressData } from 'Utilities/helpers';

import { useBiddingSection } from '../useBiddingSection';

import LocalTexts from './Valuations.texts';

import styles from './Valuations.module.scss';

interface Valuation {
	cypressId: 'retailPrice' | 'cleanPrice' | 'averagePrice';
	label: string;
	value?: string;
}

const LocalT = new MDText(LocalTexts);

const sourceTextTranslationMap: { [key: string]: string } = {
	CAP: 'priceSource.CAP',
	UKVD: 'priceSource.UKVD',
};

export const Valuations: React.FC = () => {
	const { priceAverageCondition, priceGoodCondition, priceRetail, priceSource, priceUpdatedAt } = useBiddingSection();

	const valuations = useMemo(() => {
		const opts = {
			source: LocalT.translate(`sources.${priceSource}`, {
				notFound: '',
			}),
		};

		const trim = (str: string) => str?.trim();

		return [
			{
				cypressId: 'retailPrice',
				label: trim(`${LocalT.translate('labels.retail', opts)}`),
				value: priceRetail?.displayValue,
			},
			{
				cypressId: 'cleanPrice',
				label: trim(`${LocalT.translate('labels.good', opts)}`),
				value: priceGoodCondition?.displayValue,
			},
			{
				cypressId: 'averagePrice',
				label: trim(`${LocalT.translate('labels.average', opts)}`),
				value: priceAverageCondition?.displayValue,
			},
		].filter(({ value }) => Boolean(value));
	}, [priceSource, priceRetail, priceGoodCondition, priceAverageCondition]) as Valuation[];

	const sourceText = priceSource
		? LocalT.translate(sourceTextTranslationMap[priceSource], { date: formatDate(priceUpdatedAt, 'DD/MM/YY') })
		: null;

	return (
		<Expandable
			caption={({ icon }) => (
				<div className={styles.caption}>
					<span>{LocalT.translate('expandableTitle')}</span>
					<div>{icon}</div>
				</div>
			)}
			className={styles.expandable}
			id="valuations"
			onClick={(expanded) => triggerValuationsToggleEvent(expanded)}
		>
			<ul className={styles.expandedContentContainer} role="list">
				{valuations.map(({ cypressId, label, value }) => (
					<li key={label} className={styles.valuationContainer} {...applyCypressData(cypressIds.text[cypressId])}>
						<span>{label}</span>
						<span className={styles.value}>{value}</span>
					</li>
				))}
				{sourceText ? <div className={styles.priceSource}>{sourceText}</div> : null}
			</ul>
		</Expandable>
	);
};
